/// <amd-module name="Wit/WorkItemGitLinks/Components/PullRequest/PullRequestForm" />
import * as React from "react";
import { useEffect, useRef } from "react";
import { IVssPageContext } from "VSS/Platform/Context";
import { useSubscription, useDebouncedSubscription, useDerivedObservable, useObservable } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { usePageContext } from "VSS/Platform/Layout";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { FormItem } from "VSSUI/FormItem";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { GitRepository } from "Repos/Common/Contracts/Git";
import { getGitClient } from "VC/Common/RestClient/Git";
import { IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import * as Resources from "Wit/WorkItemGitLinks/Resources";
import { pullRequestToDisplayData } from "../../GitLinkUtils";
import { ILinkTypeSpecificFormProps } from "../ILinkTypeSpecificFormProps";

const positiveIntegerPattern = /^[1-9]\d*$/;

export function PullRequestForm({ repository, onArtifactsUpdated }: ILinkTypeSpecificFormProps) {
    const pageContext = usePageContext();
    const [pullRequestText, setPullRequestText] = useObservable<string>("");
    const [matchingPr, setMatchingPr] = useObservable<IResolvedLinkDisplayData | undefined>(undefined);

    const [loading, setLoading] = useObservable(false);
    const isIdFieldDirty = useRef(false);

    const [errorMessage, setErrorMessage] = useObservable<string | undefined>(undefined);
    const error = useDerivedObservable(errorMessage, message => !!message, []);

    /**
     * Find and update the matching PR based on the current repository and PR ID
     */
    const findAndUpdateMatchingPR = async (pullRequestIdText: string) => {
        if (pullRequestIdText.length > 0) {
            isIdFieldDirty.current = true;
        }

        if (!positiveIntegerPattern.test(pullRequestIdText)) {
            if (isIdFieldDirty.current) {
                setErrorMessage(Resources.InvalidPullRequestIDError);
            }
            setMatchingPr(undefined);
            return;
        }

        if (!repository) {
            return; // Don't search yet
        }

        const pullRequestId = +pullRequestIdText;
        setLoading(true);
        setErrorMessage(undefined);
        const prDisplayData = await findMatchingPullRequest(pageContext, repository, pullRequestId);
        if (pullRequestText.value !== pullRequestIdText) {
            // The text has been changed since this request was fired - do nothing with the response.
            return;
        }

        setLoading(false);

        if (!prDisplayData) {
            setErrorMessage(format(Resources.PullRequestNotFoundError, pullRequestIdText));
            setMatchingPr(undefined);
            return;
        }

        setErrorMessage(undefined);
        setMatchingPr(prDisplayData);
    };

    // Search for matches when the text field changes (debounced)
    useDebouncedSubscription(pullRequestText, 300, findAndUpdateMatchingPR, [repository, pageContext]);

    // Search for matches when the repository changes
    useEffect(() => {
        findAndUpdateMatchingPR(pullRequestText.value);
    }, [repository]);

    // When the matching PR changes, fire the callback
    useSubscription(matchingPr, newPr => onArtifactsUpdated(newPr ? [newPr] : []));

    return (
        <FormItem label={Resources.PullRequestIDFieldLabel} message={errorMessage} error={error}>
            <div className="pull-request-form-id-row">
                <TextField
                    placeholder={Resources.PullRequestIDFieldPlaceholder}
                    value={pullRequestText}
                    onChange={(e, val) => setPullRequestText(val)}
                />
                <ConditionalChildren renderChildren={loading}>
                    <Spinner className="pull-request-loading-spinner" />
                </ConditionalChildren>
            </div>
        </FormItem>
    );
}

async function findMatchingPullRequest(
    pageContext: IVssPageContext,
    repository: GitRepository,
    pullRequestId: number
): Promise<IResolvedLinkDisplayData | undefined> {
    const gitClient = getGitClient(pageContext);
    try {
        const matchingPr = await gitClient.getPullRequestById(pullRequestId, repository.project.id);
        return pullRequestToDisplayData(pageContext, matchingPr, repository);
    } catch (e) {
        return undefined;
    }
}
